<template>
  <div ref="rootEl" class="frame-lookup">
    <label class="label-caption">Pessoa (Código / Nome)</label>

    <div class="row-frame">
      <!-- CÓDIGO -->
      <input
        type="text"
        class="frame-code input-right"
        v-model="edCdCadastro"
        v-number
        maxlength="10"
        @keyup.enter="onEnterCodigo"
        @blur="onBlurCodigo"
      />

      <!-- MIOLO (cresce) -->
      <div class="frame-body" :class="{ 'is-search': modoPesquisa }" style="position: relative;">
        <!-- VISUALIZAÇÃO -->
        <div
          v-if="!modoPesquisa"
          class="field-label frame-label"
          @click="ativarPesquisa"
          :title="nmPessoa || ''"
        >
          {{ nmPessoa }}
        </div>

        <!-- PESQUISA -->
        <input
          v-else
          type="text"
          id="nmPesquisa"
          ref="nmPesquisaInput"
          class="frame-search"
          v-model="edNmPesquisa"
          maxlength="80"
          v-uppercase
          autocomplete="off"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="confirmActive"
          @keydown.esc.prevent="cancelarPesquisa"
          @blur="onBlurPesquisa"
        />

        <!-- DROPDOWN (agora fica ancorado no miolo) -->
        <div v-if="modoPesquisa && openList" class="lookup-panel">
          <div v-if="isLoading" class="ac-loading">Buscando…</div>

          <template v-else>
            <div v-if="lsDados.length === 0" class="ac-empty">Nenhum registro encontrado</div>

            <ul class="lookup-list">
              <li
                v-for="(item, idx) in lsDados"
                :key="item.id || idx"
                class="lookup-item"
                :class="{ 'is-active': idx === activeIndex }"
                @mousedown.prevent="selecionar(item)"
                @mousemove="activeIndex = idx"
                :title="item.nmpessoa"
              >
                <span class="lookup-item-name">{{ item.nmpessoa }}</span>
                <span v-if="item.nmmunicipio" class="lookup-item-sub">
                  {{ item.nmmunicipio }} / {{ item.uf }}
                </span>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <!-- CANCELAR -->
      <button
        v-if="modoPesquisa"
        type="button"
        class="button-close frame-cancel"
        @click="cancelarPesquisa"
      />
    </div>
  </div>
</template>


<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { Api } from '@/funcoes/apiExport'
import { Funcoes } from '@/funcoes/utils'
import { globalStore } from '@/globals'

const emit = defineEmits(['update:idSelecionado', 'update:objSelecionado']);
const props = defineProps({
    initId: { type: Number, default: null},
});

const edCdCadastro = ref(null)
const edNmPesquisa = ref(null)
const nmPessoa = ref(null)
const modoPesquisa = ref(false)

const isLoading = ref(false)
const lsDados = ref([])
const openList = ref(false)
const activeIndex = ref(-1)
const NAME_MIN = 3

let debounceHandle = null
let debounceHandleNome = null
let lastQueriedCod = null
let programmatic = false
const rootEl = ref(null)
const nmPesquisaInput = ref(null)


function atualizarSelecao(obj) {
    nmPessoa.value = obj?.nmpessoa ?? null
    if (programmatic) lastQueriedCod = obj?.cdpessoa ?? null
    edCdCadastro.value = obj?.cdpessoa ?? null
    emit('update:idSelecionado', obj?.id ?? null)
    emit('update:objSelecionado', obj ?? null)
}

function limparSelecao() {
    nmPessoa.value = null
    emit('update:idSelecionado', null)
    emit('update:objSelecionado', null)
}


async function lerDados() {
    const pesquisandoPorNome = !!modoPesquisa.value

    if (!programmatic){
        if (pesquisandoPorNome) {
            const q = (edNmPesquisa.value ?? '').trim();
            if (!q) { lsDados.value = []; openList.value = false; return }
        } else {
            const cod = Funcoes.integerFromString(edCdCadastro.value)
            if (cod < 1) return
            if (cod === lastQueriedCod) return
            lastQueriedCod = cod
        }
    }

    try {
        isLoading.value = true;
        const termoPesquisa = pesquisandoPorNome ? (edNmPesquisa.value ?? '').trim() : null;
        const nmpesquisar = termoPesquisa === '?' ? '%' : termoPesquisa || null;
        const payload = {
            id: (programmatic) ? props.initId : null,
            idmunicipio: globalStore.idmunicipio,
            cdcadastro: pesquisandoPorNome ? null : Funcoes.integerFromString(edCdCadastro.value),
            nmpesquisar: pesquisandoPorNome ? nmpesquisar : null,
            ativo: 'S'
        }

        const resp = await Api.genericPost('/listas/gabinete_pessoas', payload)
        lsDados.value = Array.isArray(resp) ? resp : []

        if (programmatic){
            if (lsDados.value.length === 1) {
                atualizarSelecao(lsDados.value[0]);
            }

        }else if (!pesquisandoPorNome) {
            if (lsDados.value.length === 1) {
                selecionar(lsDados.value[0])
            } else if (lsDados.value.length === 0) {
                limparSelecao()
            } else {
                openList.value = true
                activeIndex.value = lsDados.value.length ? 0 : -1
            }
        } else {
            openList.value = true
            activeIndex.value = lsDados.value.length ? 0 : -1
        }
    } finally {
        isLoading.value = false;
        setTimeout(() => { programmatic = false }, 0);
    }
}


function onEnterCodigo() {
    clearTimeout(debounceHandle)
    lerDados()
}


function onBlurCodigo() {
    const cod = Funcoes.integerFromString(edCdCadastro.value)
    if (cod !== lastQueriedCod) {
        clearTimeout(debounceHandle)
        lerDados()
    }
}


watch(edCdCadastro, (v) => {
    clearTimeout(debounceHandle)
    const cod = Funcoes.integerFromString(v)

    if (cod !== lastQueriedCod) {
        nmPessoa.value = null
        emit('update:idSelecionado', null)
        emit('update:objSelecionado', null)
    }

    if (cod <= 0 || cod === lastQueriedCod) return
    debounceHandle = setTimeout(() => lerDados(), 350)
})


function ativarPesquisa() {
    modoPesquisa.value = true
    edNmPesquisa.value = ''
    lsDados.value = []
    openList.value = false
    activeIndex.value = -1
    lastQueriedCod = null
    clearTimeout(debounceHandle)
    nextTick(() => nmPesquisaInput.value?.focus())
}


function cancelarPesquisa() {
    modoPesquisa.value = false
    openList.value = false
    lsDados.value = []
    activeIndex.value = -1
    edNmPesquisa.value = ''
}


function onBlurPesquisa() {
    setTimeout(() => { openList.value = false }, 120)
}


function move(delta) {
    if (!openList.value || lsDados.value.length === 0) return
    const next = activeIndex.value < 0 ? 0 : activeIndex.value + delta
    const max = lsDados.value.length - 1
    activeIndex.value = Math.max(0, Math.min(next, max))
}


function confirmActive() {  
    if (activeIndex.value >= 0 && lsDados.value[activeIndex.value]) {
        selecionar(lsDados.value[activeIndex.value])
    } else if (lsDados.value.length === 1) {
        selecionar(lsDados.value[0])
    }
}


function selecionar(item) {
    atualizarSelecao(item)
    cancelarPesquisa()
}


function onDocClick(e) {
    if (!modoPesquisa.value) return
    if (!rootEl.value) return
    if (!rootEl.value.contains(e.target)) cancelarPesquisa()
}


watch(edNmPesquisa, (v) => {
    if (!modoPesquisa.value) return
    clearTimeout(debounceHandleNome)

    const q = (v ?? '').trim()
    if (q.length < NAME_MIN && q != "?") {
        lsDados.value = []
        openList.value = false
        activeIndex.value = -1
        return
    }

    openList.value = true
    activeIndex.value = -1
    debounceHandleNome = setTimeout(() => {
        lerDados()
    }, 400)
})


watch(() => props.initId, (novo) => {
    if (novo && novo > 0) {
        programmatic = true;
        lerDados();
    } else { limparSelecao(); edCdCadastro.value = null; lastQueriedCod = null }
})


onMounted(() => {
    if (props.initId && props.initId > 0) {
        programmatic = true;
        lerDados();
    }
})

onMounted(() => document.addEventListener('mousedown', onDocClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick))
</script>

<style scoped src="@/assets/css/layoutFrames.css"></style>
